import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { track } from "../lib/analytics";
import usePageMeta from "../hooks/usePageMeta";

function DeliveryRequestForm() {
  const [form, setForm] = useState({ email: "", name: "", preferred_area: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/leads", { ...form, source: "delivery_request", marketing_opt_in: false });
      track("request_delivery", { source: "pickup_page" });
      setSent(true);
      toast.success("Request received — we'll follow up about delivery arrangements.");
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  if (sent) return <p className="text-sm text-[#A35847]" data-testid="delivery-request-success">Thanks — we'll be in touch to arrange delivery and confirm any fee before you pay anything.</p>;
  return (
    <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
      <input data-testid="delivery-request-email" className="input-line flex-1" type="email" required placeholder="you@example.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} aria-label="Email for delivery request" />
      <input data-testid="delivery-request-area" className="input-line flex-1" required placeholder="Your area (e.g. Uptown, Fort Worth)" value={form.preferred_area} onChange={(e) => setForm({ ...form, preferred_area: e.target.value })} aria-label="Delivery area" />
      <button data-testid="delivery-request-submit" className="btn-ink shrink-0 min-h-[44px]" disabled={busy}>{busy ? "Sending…" : "Request delivery"}</button>
    </form>
  );
}

export default function Pickup() {
  const [locations, setLocations] = useState([]);
  const [q, setQ] = useState("");
  usePageMeta("Dallas Juice Pickup Locations & DFW Delivery | Tiff Wellness Co.", "Pick up at SandersFit Dallas or South Side on Lamar — weekday pickup days with times coordinated after your order — or request Dallas–Fort Worth delivery.", {
    schemaType: "CollectionPage",
    jsonLd: locations.length ? { "@context": "https://schema.org", "@type": "ItemList",
      itemListElement: locations.map((l, i) => ({ "@type": "ListItem", position: i + 1, name: l.name, url: `${window.location.origin}/pickup/${l.slug}` })) } : null,
  });
  useEffect(() => { api.get("/locations").then((r) => setLocations(r.data)); }, []);
  const filtered = locations.filter((l) => (l.name + l.neighborhood).toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="max-w-5xl mx-auto px-4 py-12">
      <p className="eyebrow mb-2">Pickup</p>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl mb-3">Find Your Dallas Juice Pickup</h1>
      <p className="text-sm text-[#171715]/70 max-w-2xl mb-6">Pick up at SandersFit Dallas or South Side on Lamar. Pickup days: Monday, Wednesday &amp; Thursday evenings · Tuesday &amp; Friday all day. Exact pickup times are coordinated with you after your order is confirmed — please wait for your ready notification. A partnership location is a pickup point, not a public storefront.</p>
      <div className="card-paper p-5 mb-8 max-w-2xl" data-testid="delivery-request-block">
        <p className="eyebrow mb-1">Delivery</p>
        <h2 className="text-lg font-medium mb-2">Dallas–Fort Worth delivery by arrangement</h2>
        <p className="text-sm text-[#171715]/70 mb-3">Fees and timing are confirmed with you during ordering — you're never charged an unknown fee at checkout. Tell us where you are and we'll follow up.</p>
        <DeliveryRequestForm />
      </div>
      <input data-testid="pickup-search-input" className="input-line max-w-md mb-8" placeholder="Search by name or neighborhood" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search pickup locations" />
      <div className="grid sm:grid-cols-2 gap-4" data-testid="pickup-location-list">
        {filtered.map((l) => (
          <Link to={`/pickup/${l.slug}`} key={l.id} data-testid={`pickup-hub-card-${l.id}`} className="card-paper p-5 hover:border-[#A35847]/60 block">
            <div className="flex justify-between items-start gap-2">
              <h2 className="text-xl font-display">{l.name}</h2>
              {l.status === "active_partner" && <span className="text-[10px] font-mono uppercase text-[#A35847] border border-[#A35847]/40 px-1.5 py-0.5">Active partner</span>}
            </div>
            <p className="text-sm text-[#171715]/70 mt-1">{l.neighborhood}</p>
            {l.address && <p className="text-xs text-[#171715]/60 mt-1">{l.address}</p>}
            {l.windows.length > 0 ? (
              <ul className="text-xs text-[#171715]/70 mt-3 space-y-1">
                {l.windows.map((w) => <li key={w.id} className="font-mono">{w.label}</li>)}
              </ul>
            ) : (
              <p className="text-xs text-[#A35847] mt-3 font-medium" data-testid={`pickup-nonbookable-note-${l.id}`}>Pickup days pending confirmation — exact times are coordinated after your order is confirmed.</p>
            )}
            <p className="text-xs text-[#171715]/55 mt-2">{l.cutoff_hours && l.cutoff_confirmed ? `Ordering cutoff: ${l.cutoff_hours}h before pickup · ${l.timezone}` : `Orders need at least 24 hours' notice · ${l.timezone}`}</p>
          </Link>
        ))}
        {filtered.length === 0 && <p className="text-sm text-[#171715]/60">No locations match your search.</p>}
      </div>
    </div>
  );
}

export function PickupLocationDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/locations/${slug}/slots`).then((r) => setData(r.data)).catch(() => setData(false)); }, [slug]);
  usePageMeta(data?.location ? `Juice Pickup at ${data.location.name} | Tiff. Wellness Co.` : "Pickup | Tiff. Wellness Co.",
    data?.location ? `Pick up Tiff. Wellness Co. juices at ${data.location.name} in ${data.location.neighborhood}. View the address, available pickup windows, ordering notice and access instructions.` : "",
    { jsonLd: data?.location ? {
        "@context": "https://schema.org", "@type": "Service",
        name: `Juice pickup at ${data.location.name}`,
        serviceType: "Order pickup point",
        provider: { "@type": "Organization", name: "tiff. wellness co." },
        areaServed: "Dallas, TX",
        availableChannel: { "@type": "ServiceChannel",
          serviceLocation: { "@type": "Place", name: data.location.name,
            address: { "@type": "PostalAddress", streetAddress: data.location.address, addressLocality: "Dallas", addressRegion: "TX", addressCountry: "US" } } },
      } : null });
  if (data === false) return <div className="max-w-3xl mx-auto px-4 py-20 text-center"><h1 className="text-3xl">Location not found</h1><Link className="underline text-sm" to="/pickup">All locations</Link></div>;
  if (!data) return <div className="py-20 text-center text-sm">Loading…</div>;
  const l = data.location;
  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <nav aria-label="Breadcrumb" className="text-xs text-[#171715]/60 mb-4"><Link className="underline" to="/pickup">Pickup Locations</Link> / {l.name}</nav>
      <h1 className="text-4xl sm:text-5xl mb-2">Juice Pickup at {l.name}</h1>
      {l.status === "active_partner" && <p className="text-xs font-mono uppercase text-[#A35847] mb-4">Authorized pickup partner — a pickup point inside the partner location, not a Tiff Wellness Co. storefront</p>}
      {l.status === "test" && <p className="text-xs font-mono uppercase text-[#9c3b2a] mb-4">Sample test location — not public</p>}
      <dl className="card-paper p-5 text-sm space-y-2">
        <div><dt className="font-medium">Neighborhood</dt><dd className="text-[#171715]/75">{l.neighborhood}</dd></div>
        <div><dt className="font-medium">Address</dt><dd className="text-[#171715]/75">{l.address}</dd></div>
        <div><dt className="font-medium">Access</dt><dd className="text-[#171715]/75">{l.access_requirements}</dd></div>
        <div><dt className="font-medium">Pickup instructions</dt><dd className="text-[#171715]/75">{l.pickup_instructions}</dd></div>
        <div><dt className="font-medium">Ordering notice</dt><dd className="text-[#171715]/75">{l.cutoff_hours && l.cutoff_confirmed ? `${l.cutoff_hours} hours before each pickup day (${l.timezone})` : `At least 24 hours before your pickup window — pickup is subject to availability and confirmed scheduling (${l.timezone})`}</dd></div>
      </dl>
      <h2 className="text-xl mt-8 mb-3">Upcoming windows</h2>
      {data.slots.length === 0 && <p className="text-sm text-[#171715]/70 card-paper p-4" data-testid="location-no-slots">No bookable pickup days right now. Exact pickup times are coordinated after your order is confirmed — please wait for your ready notification.</p>}
      <ul className="grid sm:grid-cols-2 gap-2">
        {data.slots.map((s) => (
          <li key={`${s.window_id}${s.date}`} className={`card-paper p-3 text-sm ${s.available ? "" : "opacity-50"}`} data-testid={`location-slot-${s.window_id}-${s.date}`}>
            <span className="font-medium block">{s.date}</span>
            <span className="text-xs text-[#171715]/70">{s.label} · {s.available ? `${s.remaining} spots` : "Full"}</span>
          </li>
        ))}
      </ul>
      <Link to="/build-your-stack" className="btn-ink mt-8 inline-flex" data-testid="location-build-cta">Build Your Stack</Link>
    </div>
  );
}
